/*
  bfroz-stores.js : port vanilla de _source/src/pages/PointsDeVente.tsx (liste + sélection + bouton de
  repli de la liste) et de _source/src/components/store-locator/StoreLocatorMap.tsx (Leaflet 1.9.4).
  Mêmes maths et mêmes minuteries : vue initiale [45.30, -74.18] zoom 10, flyTo zoom 13 en 0,8 s,
  popup ouvert à la fin du vol, repli de la liste en 300 ms (transition CSS de la source).

  Données (dans cet ordre) :
  1. les <script type="application/json" data-bfroz-stores> de la page, dans l'ordre du DOM : un par section
     `stores-list` (blocs éditables dans l'éditeur de thème). Entrée : { id, name, address, coords, name_en }.
     `coords` = « latitude, longitude » (texte saisi par le client) ; vide → géocodage ci-dessous.
  2. s'il n'y a AUCUN data-bfroz-stores sur la page : repli sur cfg.dataUrl (assets/bfroz-stores.json,
     ancien format { name, address, city, lat, lng, name_en?, address_en? }), pour compatibilité.

  Géocodage (coordonnées vides) : Nominatim (OpenStreetMap), 1 requête par seconde maximum (politique
  d'usage), résultat gardé en localStorage (clé = adresse) pour ne pas refaire l'appel. Le détaillant est
  dans la liste tout de suite ; son marqueur apparaît quand la réponse arrive. Sans réponse : pas de marqueur
  (le client peut saisir les coordonnées à la main). Les 76 détaillants d'origine ont leurs coordonnées :
  aucune requête n'est faite tant que le client n'ajoute pas un détaillant sans coordonnées.

  Tuiles : cfg.tileUrl / cfg.tileAttribution (réglages de la section), repli sur Esri World Light Gray
  (sans clé ; natif jusqu'au zoom 16, au-delà Leaflet agrandit les tuiles 16 : maxNativeZoom). Le fond
  CartoDB de la source affiche « API KEY REQUIRED » depuis 2026-09 (sur bfroz.com aussi).

  Éditeur de thème : rejoue l'initialisation sur `shopify:section:load` de la carte ; recharge les données
  quand une section stores-list est chargée / retirée / déplacée ; `shopify:block:select` sur un bloc
  détaillant sélectionne ce détaillant sur la carte. API : window.BFROZ.stores.init(root) / refresh().

  Différences volontaires avec la source (sans effet sur l'aspect au repos) :
  - deux <linearGradient> à id distincts (marqueur actif / inactif) : la source réutilise l'id « pg » pour
    tous les marqueurs, donc le navigateur résout toujours le PREMIER, et le marqueur sélectionné n'était
    jamais vraiment cyan ;
  - map.invalidateSize() quand le conteneur change de taille (repli de la liste, liste injectée) ;
  - le clic sur « Itinéraire » ne désélectionne pas le détaillant (stopPropagation).

  Classes Tailwind basculées ici : écrites en clair (le build scanne ce fichier).
*/
(function () {
  'use strict';

  var BFROZ = (window.BFROZ = window.BFROZ || {});

  var CLS_BTN_SELECTED = ['border-l-2', 'border-l-hot-cyan', 'bg-hot-cyan/8'];
  var CLS_BTN_IDLE = ['hover:bg-chill-blue/3'];
  var CLS_ICON_WRAP_SELECTED = ['bg-hot-cyan/20'];
  var CLS_ICON_WRAP_IDLE = ['bg-chill-blue/6'];
  var CLS_ICON_SELECTED = ['text-hot-cyan'];
  var CLS_ICON_IDLE = ['text-chill-blue/40'];
  var CLS_PANEL_OPEN = 'lg:w-[340px]';
  var CLS_PANEL_CLOSED = 'lg:w-0';
  var PANEL_WIDTH = 340;

  var TILE_URL = 'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}';
  var TILE_ATTRIBUTION = 'Tiles &copy; Esri &mdash; Esri, DeLorme, NAVTEQ';
  var TILE_MAX_NATIVE_ZOOM = 16; // Esri Light Gray : pas de tuiles au-delà de 16
  var MAP_MAX_ZOOM = 18;
  var DEFAULT_CENTER = [45.3, -74.18];
  var DEFAULT_ZOOM = 10;
  var SELECT_ZOOM = 13;
  var FLY_DURATION = 0.8;

  var GEOCODE_URL = 'https://nominatim.openstreetmap.org/search';
  var GEOCODE_MIN_INTERVAL = 1100; // ms entre deux requêtes (politique Nominatim : 1/s maximum)
  var GEOCODE_CACHE_PREFIX = 'bfroz:geo:';
  var GEOCODE_NEGATIVE_TTL = 24 * 60 * 60 * 1000; // « adresse introuvable » gardé 24 h, puis on réessaie

  function addClasses(el, list) {
    for (var i = 0; i < list.length; i++) el.classList.add(list[i]);
  }
  function removeClasses(el, list) {
    for (var i = 0; i < list.length; i++) el.classList.remove(list[i]);
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // Adresse sur une seule ligne (lignes du textarea jointes par « , ») : itinéraire, géocodage.
  function oneLine(address) {
    return String(address || '')
      .split(/\s*\r?\n+\s*/)
      .map(function (s) { return s.trim(); })
      .filter(Boolean)
      .join(', ');
  }

  function directionsUrl(address) {
    return 'https://www.google.com/maps/dir/?api=1&destination=' + encodeURIComponent(oneLine(address));
  }

  // « 45.2592, -74.1290 » (Google Maps), aussi « 45.2592 -74.1290 », « 45,2592 ; -74,1290 » (virgule décimale).
  function parseCoords(value) {
    if (typeof value !== 'string') return null;
    var parts = value.trim().split(/[;,\s]+/).filter(Boolean);
    var lat, lng;
    if (parts.length === 2) {
      lat = parseFloat(parts[0]);
      lng = parseFloat(parts[1]);
    } else if (parts.length === 4 && /^-?\d+$/.test(parts[0]) && /^\d+$/.test(parts[1]) && /^-?\d+$/.test(parts[2]) && /^\d+$/.test(parts[3])) {
      lat = parseFloat(parts[0] + '.' + parts[1]);
      lng = parseFloat(parts[2] + '.' + parts[3]);
    } else {
      return null;
    }
    if (isNaN(lat) || isNaN(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
    return { lat: lat, lng: lng };
  }

  // Même SVG que createIcon() de la source ; seul l'id du dégradé change selon l'état.
  function createIcon(selected) {
    var id = selected ? 'bfroz-pin-on' : 'bfroz-pin-off';
    var top = selected ? '#00BDD1' : '#7FCEDD';
    var bottom = selected ? '#009aad' : '#5BB8CC';
    return window.L.divIcon({
      className: '',
      iconSize: [30, 40],
      iconAnchor: [15, 40],
      popupAnchor: [0, -40],
      html:
        '<svg width="30" height="40" viewBox="0 0 30 40" xmlns="http://www.w3.org/2000/svg">' +
        '<defs><linearGradient id="' + id + '" x1="0" y1="0" x2="0" y2="1">' +
        '<stop offset="0%" stop-color="' + top + '"/>' +
        '<stop offset="100%" stop-color="' + bottom + '"/>' +
        '</linearGradient></defs>' +
        '<path d="M15 0C6.72 0 0 6.72 0 15c0 11.25 15 23 15 23s15-11.75 15-23C30 6.72 23.28 0 15 0z" fill="url(#' + id + ')" style="filter: drop-shadow(0 2px 4px rgba(0,86,119,0.3))"/>' +
        '<circle cx="15" cy="13.5" r="5.5" fill="white" fill-opacity="0.95"/>' +
        '</svg>'
    });
  }

  // Même HTML que createPopupHtml() de la source (les sauts de ligne de l'adresse deviennent des <br>).
  function createPopupHtml(store, label) {
    var name = escapeHtml(store.name || '');
    var address = escapeHtml(store.address || '').replace(/\r?\n/g, '<br>');
    return (
      '<div style="min-width:200px;font-family:Figtree,system-ui,sans-serif;color:#005677;">' +
      '<p style="margin:0;font-family:Anton,sans-serif;font-size:0.875rem;letter-spacing:0.08em;text-transform:uppercase;line-height:1.2;">' + name + '</p>' +
      '<p style="margin:6px 0 0;font-size:0.75rem;line-height:1.5;color:rgba(0,86,119,0.7);">' + address + '</p>' +
      '<a href="' + directionsUrl(store.address) + '" target="_blank" rel="noopener noreferrer" style="display:flex;margin-top:12px;align-items:center;justify-content:center;gap:6px;border-radius:8px;background:#005677;padding:10px 12px;font-size:11px;font-weight:700;letter-spacing:0.08em;text-transform:uppercase;color:#ffffff;text-decoration:none;">' +
      '<span style="font-size:12px;line-height:1;">\u2197</span>' +
      escapeHtml(label) +
      '</a>' +
      '</div>'
    );
  }

  function readConfig(root) {
    var node = root.querySelector('[data-stores-config]');
    var cfg = {};
    if (node) {
      try {
        cfg = JSON.parse(node.textContent || '{}');
      } catch (e) {
        console.error('[bfroz-stores] configuration illisible', e);
      }
    }
    cfg.labels = cfg.labels || {};
    var lat = parseFloat(cfg.center && cfg.center.lat);
    var lng = parseFloat(cfg.center && cfg.center.lng);
    cfg.centerLatLng = isNaN(lat) || isNaN(lng) ? DEFAULT_CENTER : [lat, lng];
    var z = parseInt(cfg.zoom, 10);
    cfg.zoomLevel = isNaN(z) ? DEFAULT_ZOOM : z;
    cfg.isEnglish = String(cfg.locale || '').toLowerCase().indexOf('en') === 0;
    cfg.tileUrl = typeof cfg.tileUrl === 'string' && cfg.tileUrl.trim() ? cfg.tileUrl.trim() : TILE_URL;
    cfg.tileAttribution = typeof cfg.tileAttribution === 'string' && cfg.tileAttribution.trim() ? cfg.tileAttribution.trim() : TILE_ATTRIBUTION;
    return cfg;
  }

  // Entrée brute (bloc ou fichier) → { id, name, address, lat|null, lng|null }. Le nom est obligatoire ;
  // sans coordonnées valides, lat/lng restent null (géocodage plus tard si l'adresse est remplie).
  function normalize(list, isEnglish) {
    var out = [];
    if (!Array.isArray(list)) return out;
    for (var i = 0; i < list.length; i++) {
      var s = list[i] || {};
      var name = typeof s.name === 'string' ? s.name.trim() : '';
      if (!name) continue;
      var nameEn = typeof s.name_en === 'string' ? s.name_en.trim() : '';
      var address = typeof s.address === 'string' ? s.address.trim() : '';
      var addressEn = typeof s.address_en === 'string' ? s.address_en.trim() : '';
      var c = parseCoords(s.coords);
      if (!c && s.lat != null && s.lng != null) {
        var lat = parseFloat(s.lat);
        var lng = parseFloat(s.lng);
        if (!isNaN(lat) && !isNaN(lng)) c = { lat: lat, lng: lng };
      }
      out.push({
        id: typeof s.id === 'string' ? s.id : '',
        name: isEnglish && nameEn ? nameEn : name,
        address: isEnglish && addressEn ? addressEn : address,
        lat: c ? c.lat : null,
        lng: c ? c.lng : null
      });
    }
    return out;
  }

  // 1. Blocs des sections stores-list, dans l'ordre du DOM. null = aucune section stores-list sur la page.
  function collectFromDom() {
    var nodes = document.querySelectorAll('script[data-bfroz-stores]');
    if (!nodes.length) return null;
    var list = [];
    for (var i = 0; i < nodes.length; i++) {
      try {
        var data = JSON.parse(nodes[i].textContent || '[]');
        if (Array.isArray(data)) list = list.concat(data);
      } catch (e) {
        console.error('[bfroz-stores] liste de détaillants illisible (section ' + (nodes[i].getAttribute('data-section-id') || '?') + ')', e);
      }
    }
    return list;
  }

  // 2. Repli : fichier bfroz-stores.json (ancien format).
  function loadFromFile(cfg, done) {
    if (!cfg.dataUrl || typeof window.fetch !== 'function') return done([]);
    // Si l'URL de l'asset pointe vers un autre hôte (proxy de développement, CDN sans CORS),
    // on retente le même chemin sur l'origine de la page (/cdn/shop/t/.../assets/bfroz-stores.json).
    var urls = [cfg.dataUrl];
    try {
      var abs = new URL(cfg.dataUrl, window.location.href);
      if (abs.host !== window.location.host) urls.push(window.location.origin + abs.pathname + abs.search);
    } catch (e) { /* URL invalide : on garde la première */ }
    function attempt(i) {
      if (i >= urls.length) {
        console.error('[bfroz-stores] impossible de charger ' + cfg.dataUrl);
        return done([]);
      }
      window
        .fetch(urls[i], { credentials: 'omit' })
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.json();
        })
        .then(function (data) {
          done(normalize(data, cfg.isEnglish));
        })
        .catch(function (err) {
          console.warn('[bfroz-stores] échec ' + urls[i], err && err.message);
          attempt(i + 1);
        });
    }
    attempt(0);
  }

  function loadStores(cfg, done) {
    var fromDom = collectFromDom();
    if (fromDom !== null) return done(normalize(fromDom, cfg.isEnglish));
    loadFromFile(cfg, done);
  }

  // ---------- géocodage Nominatim : file unique, 1 requête à la fois, espacées de GEOCODE_MIN_INTERVAL ----------
  var geo = { queue: [], inflight: {}, busy: false, lastAt: 0 };

  function geoKey(query) {
    return GEOCODE_CACHE_PREFIX + query.toLowerCase().replace(/\s+/g, ' ').trim();
  }
  function geoCacheGet(query) {
    try {
      var raw = window.localStorage.getItem(geoKey(query));
      if (!raw) return null;
      var v = JSON.parse(raw);
      if (v && typeof v.lat === 'number' && typeof v.lng === 'number') return v;
      if (v && v.none && Date.now() - (v.t || 0) < GEOCODE_NEGATIVE_TTL) return v;
    } catch (e) { /* localStorage indisponible ou valeur corrompue */ }
    return null;
  }
  function geoCacheSet(query, value) {
    try {
      window.localStorage.setItem(geoKey(query), JSON.stringify(value));
    } catch (e) { /* quota ou mode privé : on vit sans cache */ }
  }

  function geocode(query, cb) {
    query = oneLine(query);
    if (!query || typeof window.fetch !== 'function') return cb(null);
    var cached = geoCacheGet(query);
    if (cached) return cb(cached.none ? null : cached);
    if (geo.inflight[query]) {
      geo.inflight[query].push(cb);
      return;
    }
    geo.inflight[query] = [cb];
    geo.queue.push(query);
    geoPump();
  }

  function geoPump() {
    if (geo.busy || !geo.queue.length) return;
    geo.busy = true;
    var wait = Math.max(0, geo.lastAt + GEOCODE_MIN_INTERVAL - Date.now());
    setTimeout(function () {
      var query = geo.queue.shift();
      geo.lastAt = Date.now();
      var url = GEOCODE_URL + '?format=json&limit=1&countrycodes=ca&q=' + encodeURIComponent(query);
      function finish(value) {
        var cbs = geo.inflight[query] || [];
        delete geo.inflight[query];
        geo.busy = false;
        for (var i = 0; i < cbs.length; i++) cbs[i](value);
        geoPump();
      }
      window
        .fetch(url, { headers: { Accept: 'application/json' } })
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.json();
        })
        .then(function (data) {
          var hit = Array.isArray(data) && data.length ? data[0] : null;
          var lat = parseFloat(hit && hit.lat);
          var lng = parseFloat(hit && hit.lon);
          if (!isNaN(lat) && !isNaN(lng)) {
            var value = { lat: lat, lng: lng, t: Date.now() };
            geoCacheSet(query, value);
            finish(value);
          } else {
            console.warn('[bfroz-stores] adresse introuvable (Nominatim) : ' + query);
            geoCacheSet(query, { none: true, t: Date.now() });
            finish(null);
          }
        })
        .catch(function (err) {
          // erreur réseau / limite atteinte : rien en cache, on réessaiera à la prochaine visite
          console.warn('[bfroz-stores] géocodage échoué : ' + query, err && err.message);
          finish(null);
        });
    }, wait);
  }

  BFROZ.geocode = geocode;

  function init(root) {
    if (!root || root.__bfrozStores) return;
    var L = window.L;
    if (!L) {
      console.error('[bfroz-stores] Leaflet absent (leaflet.js doit être chargé avant bfroz-stores.js)');
      return;
    }
    var panel = root.querySelector('[data-stores-panel]');
    var listEl = root.querySelector('[data-stores-list]');
    var emptyEl = root.querySelector('[data-stores-empty]');
    var toggle = root.querySelector('[data-stores-toggle]');
    var mapEl = root.querySelector('[data-stores-map]');
    var template = root.querySelector('template[data-stores-item]');
    if (!panel || !listEl || !mapEl || !template) return;

    var cfg = readConfig(root);
    var stores = [];
    var items = [];
    var markers = [];
    var selected = null;
    var listOpen = true;
    var pendingMoveEnd = null;

    // ---------- carte (StoreLocatorMap.tsx) ----------
    var map = L.map(mapEl, { scrollWheelZoom: true, zoomControl: false, maxZoom: MAP_MAX_ZOOM }).setView(cfg.centerLatLng, cfg.zoomLevel);
    L.tileLayer(cfg.tileUrl, { attribution: cfg.tileAttribution, maxNativeZoom: TILE_MAX_NATIVE_ZOOM, maxZoom: MAP_MAX_ZOOM }).addTo(map);
    var layerGroup = L.layerGroup().addTo(map);

    var state = {
      map: map,
      root: root,
      disposed: false,
      refresh: function () {},
      reload: function () {},
      selectById: function () {},
      getSelected: function () { return selected; },
      getStores: function () { return stores; }
    };
    root.__bfrozStores = state;

    function paintItem(index) {
      var it = items[index];
      if (!it) return;
      var on = selected === index;
      removeClasses(it.button, on ? CLS_BTN_IDLE : CLS_BTN_SELECTED);
      addClasses(it.button, on ? CLS_BTN_SELECTED : CLS_BTN_IDLE);
      if (it.iconWrap) {
        removeClasses(it.iconWrap, on ? CLS_ICON_WRAP_IDLE : CLS_ICON_WRAP_SELECTED);
        addClasses(it.iconWrap, on ? CLS_ICON_WRAP_SELECTED : CLS_ICON_WRAP_IDLE);
      }
      if (it.icon) {
        removeClasses(it.icon, on ? CLS_ICON_IDLE : CLS_ICON_SELECTED);
        addClasses(it.icon, on ? CLS_ICON_SELECTED : CLS_ICON_IDLE);
      }
      if (it.directions) it.directions.classList.toggle('hidden', !on);
      it.button.setAttribute('aria-pressed', on ? 'true' : 'false');
    }

    // Leaflet donne role="button" + tabindex au marqueur mais aucun nom : aria-label = nom du détaillant
    // (setIcon recrée l'élément, donc à reposer après chaque changement d'icône). Invisible à l'écran.
    function labelMarker(marker, store) {
      var el = marker && marker.getElement ? marker.getElement() : null;
      if (el && store && store.name) el.setAttribute('aria-label', store.name);
    }

    function paintMarkers() {
      for (var i = 0; i < markers.length; i++) {
        if (markers[i]) {
          markers[i].setIcon(createIcon(selected === i));
          labelMarker(markers[i], stores[i]);
        }
      }
    }

    function hasCoords(store) {
      return !!store && typeof store.lat === 'number' && typeof store.lng === 'number';
    }

    function flyToSelected() {
      if (selected === null || !hasCoords(stores[selected])) return;
      var store = stores[selected];
      var marker = markers[selected];
      if (pendingMoveEnd) {
        map.off('moveend', pendingMoveEnd);
        pendingMoveEnd = null;
      }
      pendingMoveEnd = function () {
        pendingMoveEnd = null;
        if (marker && marker._icon) marker.openPopup();
      };
      map.once('moveend', pendingMoveEnd);
      map.flyTo([store.lat, store.lng], SELECT_ZOOM, { duration: FLY_DURATION });
    }

    function select(index) {
      var previous = selected;
      selected = selected === index ? null : index;
      if (previous !== null) paintItem(previous);
      if (selected !== null) paintItem(selected);
      paintMarkers();
      // La source reconstruit tous ses marqueurs à chaque changement d'état React, ce qui ferme le popup
      // ouvert : à la désélection, on le ferme aussi (à la sélection, flyTo → moveend rouvre le bon).
      if (selected === null) map.closePopup();
      if (selected !== null) flyToSelected();
    }

    function buildList() {
      // vide la liste (sauf l'état « aucun résultat »)
      for (var k = 0; k < items.length; k++) {
        if (items[k].button.parentNode) items[k].button.parentNode.removeChild(items[k].button);
      }
      items = [];
      var frag = document.createDocumentFragment();
      for (var i = 0; i < stores.length; i++) {
        (function (index) {
          var store = stores[index];
          var node = template.content.firstElementChild.cloneNode(true);
          var nameEl = node.querySelector('[data-item-name]');
          var addressEl = node.querySelector('[data-item-address]');
          var link = node.querySelector('[data-item-directions]');
          if (nameEl) nameEl.textContent = store.name;
          if (addressEl) addressEl.textContent = store.address;
          if (link) {
            link.href = directionsUrl(store.address);
            link.addEventListener('click', function (e) {
              e.stopPropagation();
            });
          }
          if (store.id) node.setAttribute('data-store-id', store.id);
          node.addEventListener('click', function () {
            select(index);
          });
          items.push({
            button: node,
            iconWrap: node.querySelector('[data-item-icon]'),
            icon: node.querySelector('[data-item-icon] svg'),
            directions: link
          });
          frag.appendChild(node);
        })(i);
      }
      listEl.appendChild(frag);
      if (emptyEl) emptyEl.classList.toggle('hidden', stores.length > 0);
      for (var j = 0; j < items.length; j++) paintItem(j);
    }

    function addMarker(index) {
      var store = stores[index];
      if (!hasCoords(store) || markers[index]) return;
      var marker = L.marker([store.lat, store.lng], { icon: createIcon(selected === index) });
      marker.on('click', function () {
        select(index);
      });
      marker.bindPopup(createPopupHtml(store, cfg.labels.directions || ''));
      marker.addTo(layerGroup);
      labelMarker(marker, store);
      markers[index] = marker;
    }

    function buildMarkers() {
      layerGroup.clearLayers();
      markers = [];
      for (var i = 0; i < stores.length; i++) addMarker(i);
    }

    // Détaillants sans coordonnées : géocodage en arrière-plan, marqueur ajouté à la réponse.
    function geocodePending() {
      for (var i = 0; i < stores.length; i++) {
        (function (store) {
          if (hasCoords(store) || !store.address) return;
          geocode(store.address, function (value) {
            if (state.disposed || !value) return;
            var index = stores.indexOf(store); // la liste a pu être rechargée entre-temps
            if (index < 0) return;
            store.lat = value.lat;
            store.lng = value.lng;
            addMarker(index);
            if (selected === index) flyToSelected();
          });
        })(stores[i]);
      }
    }

    function setListOpen(open) {
      listOpen = open;
      map.closePopup(); // même effet que le re-rendu React de la source (marqueurs reconstruits, popup fermé)
      panel.classList.toggle(CLS_PANEL_OPEN, open);
      panel.classList.toggle(CLS_PANEL_CLOSED, !open);
      if (toggle) {
        toggle.style.left = (open ? PANEL_WIDTH : 0) + 'px';
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        toggle.setAttribute('aria-label', open ? cfg.labels.hideList || '' : cfg.labels.showList || '');
        var hideIcon = toggle.querySelector('[data-stores-toggle-hide]');
        var showIcon = toggle.querySelector('[data-stores-toggle-show]');
        if (hideIcon) hideIcon.classList.toggle('hidden', !open);
        if (showIcon) showIcon.classList.toggle('hidden', open);
      }
      // la largeur transitionne en 300 ms (duration-300) : recalculer la carte à la fin
      setTimeout(function () {
        map.invalidateSize();
      }, 320);
    }

    if (toggle) {
      toggle.addEventListener('click', function () {
        setListOpen(!listOpen);
      });
    }

    // Le conteneur change de taille quand la liste est injectée ou repliée : Leaflet n'écoute que window.resize.
    if ('ResizeObserver' in window) {
      var roTimer = null;
      new ResizeObserver(function () {
        if (roTimer) return;
        roTimer = setTimeout(function () {
          roTimer = null;
          map.invalidateSize();
        }, 80);
      }).observe(mapEl);
    }

    state.refresh = function () {
      buildList();
      buildMarkers();
      map.invalidateSize();
      geocodePending();
    };

    // Recharge les données (éditeur de thème : bloc ajouté, modifié, retiré ou déplacé).
    state.reload = function () {
      if (state.disposed) return;
      var previousId = selected !== null && stores[selected] ? stores[selected].id : '';
      selected = null;
      map.closePopup();
      loadStores(cfg, function (list) {
        if (state.disposed) return;
        stores = list;
        state.refresh();
        if (previousId) state.selectById(previousId, false);
      });
    };

    // Sélectionne (sans désélectionner) le détaillant dont le bloc a cet id ; fly = vol vers le marqueur.
    state.selectById = function (id, fly) {
      for (var i = 0; i < stores.length; i++) {
        if (stores[i].id !== id) continue;
        if (selected !== i) {
          if (fly === false) {
            selected = i;
            paintItem(i);
            paintMarkers();
          } else {
            select(i);
          }
        }
        if (items[i] && items[i].button.scrollIntoView) items[i].button.scrollIntoView({ block: 'nearest' });
        return true;
      }
      return false;
    };

    state.dispose = function () {
      state.disposed = true;
      try { map.remove(); } catch (e) { /* déjà retirée */ }
    };

    loadStores(cfg, function (list) {
      if (state.disposed) return;
      stores = list;
      state.refresh();
    });
  }

  function roots() {
    return document.querySelectorAll('[data-stores]');
  }

  function boot() {
    var all = roots();
    for (var i = 0; i < all.length; i++) init(all[i]);
  }

  function refreshAll() {
    var all = roots();
    for (var i = 0; i < all.length; i++) {
      if (all[i].__bfrozStores) all[i].__bfrozStores.reload();
      else init(all[i]);
    }
  }

  BFROZ.stores = { init: init, boot: boot, refresh: refreshAll };

  function sectionRoot(target) {
    if (!target || !target.querySelector) return null;
    return target.matches('[data-stores]') ? target : target.querySelector('[data-stores]');
  }
  function holdsStores(target) {
    return !!(target && target.querySelector && target.querySelector('[data-bfroz-stores]'));
  }

  if (!BFROZ.storesBound) {
    BFROZ.storesBound = true;

    document.addEventListener('shopify:section:load', function (e) {
      var root = sectionRoot(e.target);
      if (root) return init(root);
      if (holdsStores(e.target)) refreshAll();
    });
    document.addEventListener('shopify:section:unload', function (e) {
      var root = sectionRoot(e.target);
      if (root && root.__bfrozStores) root.__bfrozStores.dispose();
      // la section est retirée du DOM juste après l'événement : recharger au tour suivant
      if (holdsStores(e.target)) setTimeout(refreshAll, 0);
    });
    document.addEventListener('shopify:section:reorder', function (e) {
      if (holdsStores(e.target)) setTimeout(refreshAll, 0);
    });
    document.addEventListener('shopify:block:select', function (e) {
      var id = e.detail && e.detail.blockId;
      if (!id || !holdsStores(e.target)) return;
      var all = roots();
      for (var i = 0; i < all.length; i++) {
        if (all[i].__bfrozStores) all[i].__bfrozStores.selectById(id, true);
      }
    });
  }

  boot();
})();
